/**
 * 解析文档中的所有 cookies，并将它们作为对象返回。
 * @returns {Record<string, string>} 一个对象，映射了 cookie 名称到它们的值。
 */
export const getAllCookies = (): Record<string, string> => {
    return document.cookie.split('; ').reduce(
        (cookies, cookie) => {
            const [name, value] = cookie.split('=');

            try {
                cookies[name] = decodeURIComponent(value || '');
            } catch (error) {
                cookies[name] = value || '';
            }

            return cookies;
        },
        {} as Record<string, string>
    );
};

/**
 * 通过名称检索特定的 cookie 值。
 * @param {string} key 要检索的 cookie 的名称。
 * @returns {string} cookie 的值，如果未找到则为空字符串。
 */
export const getCookie = (key: string): string => {
    const cookies = getAllCookies();

    return cookies[key] || '';
};

/**
 * 设置一个 cookie。
 * @param {string} name cookie 的名称。
 * @param {string} value cookie 的值。
 * @param {object} [options] 可选的设置项，包括 expires、path、domain、secure 等。
 * @param {number | Date} [options.expires] cookie 的过期时间，可以是天数或具体的日期对象。
 * @param {string} [options.path] cookie 的路径。
 * @param {string} [options.domain] cookie 的域名。
 * @param {boolean} [options.secure] 是否只通过安全协议传输 cookie。
 * @param {boolean} [options.sameSite] SameSite 设置，可以是 'Strict'、'Lax' 或 'None'。
 */
export const setCookie = (
    name: string,
    value: string,
    options: {
        expires?: number | Date;
        path?: string;
        domain?: string;
        secure?: boolean;
        sameSite?: 'Strict' | 'Lax' | 'None';
    } = {}
) => {
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (options.expires) {
        if (typeof options.expires === 'number') {
            const d = new Date();

            d.setTime(d.getTime() + options.expires * 24 * 60 * 60 * 1000);
            cookie += `; expires=${d.toUTCString()}`;
        } else {
            cookie += `; expires=${options.expires.toUTCString()}`;
        }
    }

    if (options.path) {
        cookie += `; path=${options.path}`;
    }

    if (options.domain) {
        cookie += `; domain=${options.domain}`;
    }

    if (options.secure) {
        cookie += '; secure';
    }

    if (options.sameSite) {
        cookie += `; SameSite=${options.sameSite}`;
    }

    document.cookie = cookie;
};

/**
 * 删除一个 cookie。
 * @param {string} name 要删除的 cookie 的名称。
 * @param {object} [options] 可选的设置项，包括 path 和 domain。
 * @param {string} [options.path] cookie 的路径。
 * @param {string} [options.domain] cookie 的域名。
 */
export const removeCookie = (
    name: string,
    options: {
        path?: string;
        domain?: string;
    } = {}
) => {
    setCookie(name, '', {
        expires: new Date(0), // 设置过期时间为过去的时间
        path: options.path,
        domain: options.domain
    });
};
